import classnames from 'classnames';
import { isEqual, isNull, isUndefined } from 'lodash/fp';
import React from 'react';

import { formatDate } from '../../../../../utils/luxon';
import { CalculatorField } from '../../../../admin/calculator/store/types';
import { CalculatorDocumentInstanceRiskField, CurrencyValue, DocumentInstanceRiskField, FieldValue } from '../../../../datasets/instances/store';
import { DatasetFieldType } from '../../../../datasets/store/index';
import { newCalculatorField } from '../../../../shared/calculator/constants';
import { DropdownOption } from '../../../../shared/dropdown/Dropdown';
import { OverflowTooltip } from '../../../../shared/tooltip';
import styles from '../../Analytics.module.scss';
import { CalculatorRiskValue } from './CalculatorRiskValue';

const NO_VALUE_PLACEHOLDER = 'No Value Provided';

export const getRiskFieldElement = (fieldValue: FieldValue, type: DatasetFieldType, label: string, testId: string, showOutput = false, showGracePeriod = false) => {
    let value = NO_VALUE_PLACEHOLDER;
    const isNullOrUndefined = isNull(fieldValue) || isUndefined(fieldValue);
    if (!isNullOrUndefined) {
        if (type === DatasetFieldType.CHECKBOX) {
            value = fieldValue ? 'True' : 'False';
        }
        if ([DatasetFieldType.TEXT, DatasetFieldType.LONGTEXT].includes(type) && (fieldValue as string).length > 0) {
            value = (fieldValue as string);
        }
        if (type === DatasetFieldType.DROPDOWN) {
            value = (fieldValue as string[]).join(', ');
        }
        if (type === DatasetFieldType.DATE) {
            value = formatDate(fieldValue as string);
        }
        if (type === DatasetFieldType.NUMBER) {
            value = (fieldValue as number).toString();
        }
        if (type === DatasetFieldType.TIME) {
            if ((fieldValue as string[]).some(element => element.length > 0)) {
                value = (fieldValue as string[]).join(', ');
            }
        }
        if (type === DatasetFieldType.CURRENCY_AMOUNT) {
            const currencyField = (fieldValue as CurrencyValue);
            if (!isNull(currencyField.currency) && !isUndefined(currencyField.value)) {
                value = `${currencyField.currency}: ${currencyField.value}`;
            }
        }
        if (type === DatasetFieldType.CALCULATOR) {
            if (!isEqual([newCalculatorField()], fieldValue as CalculatorField[])) {
                return (
                    <CalculatorRiskValue label={label} showGracePeriod={showGracePeriod} showOutput={showOutput} value={fieldValue as CalculatorField[]} />
                );
            }
        }
    }
    const noValue = value === NO_VALUE_PLACEHOLDER;
    return (
        <OverflowTooltip className={classnames(styles.fieldAnswer, { [styles.fieldAnswerNoValue]: noValue })} overlayText={value} testId={`${testId}-answer`} />
    );
};

export const getFieldValueElement = (fieldValue: FieldValue, riskField: DocumentInstanceRiskField, label: string, testId: string) => {
    const { type } = riskField;
    let showGracePeriod = false;
    let showOutput = false;
    if (type === DatasetFieldType.CALCULATOR) {
        const calculatorRiskField = riskField as CalculatorDocumentInstanceRiskField;
        showGracePeriod = calculatorRiskField.showGracePeriod;
        showOutput = calculatorRiskField.showOutput;
    }
    return getRiskFieldElement(fieldValue, type, label, testId, showOutput, showGracePeriod);
};

export const getFieldValue = (fieldValue: FieldValue, type: DatasetFieldType) => {
    if (isNull(fieldValue) || isUndefined(fieldValue) || fieldValue === '') {
        return NO_VALUE_PLACEHOLDER;
    }
    if (type === DatasetFieldType.CHECKBOX) {
        return fieldValue ? 'True' : 'False';
    }
    if (type === DatasetFieldType.DROPDOWN) {
        return (fieldValue as string[]).join(', ');
    }
    if (type === DatasetFieldType.DATE) {
        return formatDate(fieldValue as string);
    }
    if (type === DatasetFieldType.NUMBER) {
        return (fieldValue as number).toString();
    }
    if (type === DatasetFieldType.TIME) {
        if ((fieldValue as string[]).every(element => element === '')) {
            return NO_VALUE_PLACEHOLDER;
        }
        return (fieldValue as string[]).join(', ');
    }
    if (type === DatasetFieldType.CURRENCY_AMOUNT) {
        const { currency, value } = (fieldValue as CurrencyValue);
        if (!isNull(currency) && !isUndefined(value)) {
            return `${currency}: ${value}`;
        }
        return NO_VALUE_PLACEHOLDER;
    }
    return NO_VALUE_PLACEHOLDER;
};

export const riskLevelOptions: DropdownOption[] = [
    { value: '20', label: 'Highly Desirable' },
    { value: '40', label: 'Desirable' },
    { value: '60', label: 'Acceptable' },
    { value: '80', label: 'Of Concern' },
    { value: '100', label: 'Of Serious Concern' }
];
